import React, { useState, useEffect } from "react";
import { Patient } from "../../PatientCard";
import LabRequisitionService, {
  LabRequisition,
} from "../../../../services/labRequisitionService";

interface LabRequisitionHistoryProps {
  patient: Patient;
  isOpen: boolean;
  onClose: () => void;
  onGeneratePDF?: (requisitionId: number) => void;
  refreshTrigger?: number;
}

const LabRequisitionHistory: React.FC<LabRequisitionHistoryProps> = ({
  patient,
  isOpen,
  onClose,
  onGeneratePDF,
  refreshTrigger,
}) => {
  const [requisitions, setRequisitions] = useState<LabRequisition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [selectedRequisition, setSelectedRequisition] =
    useState<LabRequisition | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [actionLoading, setActionLoading] = useState<{
    [key: number]: {
      issue?: boolean;
      complete?: boolean;
      cancel?: boolean;
      pdf?: boolean;
      send?: boolean;
      print?: boolean;
    };
  }>({});

  useEffect(() => {
    if (isOpen && patient.id) {
      loadRequisitions();
    }
  }, [isOpen, patient.id, refreshTrigger]);

  const loadRequisitions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await LabRequisitionService.getPatientLabRequisitions(
        patient.id
      );
      setRequisitions(result);
    } catch (err: any) {
      setError(err.message || "Failed to load lab requisitions");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (
    requisitionId: number,
    action: "issue" | "complete" | "cancel",
    actionFunction: (id: number) => Promise<LabRequisition>
  ) => {
    setActionLoading((prev) => ({
      ...prev,
      [requisitionId]: { ...prev[requisitionId], [action]: true },
    }));

    try {
      const updatedRequisition = await actionFunction(requisitionId);

      setRequisitions((prev) =>
        prev.map((req) => (req.id === requisitionId ? updatedRequisition : req))
      );

      const messages = {
        issue: "Lab requisition issued successfully",
        complete: "Lab requisition marked as completed",
        cancel: "Lab requisition cancelled",
      };

      alert(messages[action]);
    } catch (err: any) {
      alert(`Failed to ${action} requisition: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [requisitionId]: { ...prev[requisitionId], [action]: false },
      }));
    }
  };

  const handleGeneratePDF = async (requisitionId: number) => {
    setActionLoading((prev) => ({
      ...prev,
      [requisitionId]: { ...prev[requisitionId], pdf: true },
    }));

    try {
      await LabRequisitionService.generatePDF(requisitionId);

      if (onGeneratePDF) {
        onGeneratePDF(requisitionId);
      }
    } catch (err: any) {
      alert(`Failed to open print view: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [requisitionId]: { ...prev[requisitionId], pdf: false },
      }));
    }
  };

  const handleGenerateAndPrint = async (requisitionId: number) => {
    setActionLoading((prev) => ({
      ...prev,
      [requisitionId]: { ...prev[requisitionId], print: true },
    }));

    try {
      const printUrl = `${process.env.REACT_APP_API_URL}/lab/${requisitionId}`;
      const printWindow = window.open(printUrl, "_blank");

      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      } else {
        alert(
          "Please allow pop-ups for this site to enable automatic printing."
        );
      }
    } catch (err: any) {
      alert(`Failed to open print view: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [requisitionId]: { ...prev[requisitionId], print: false },
      }));
    }
  };

  if (!isOpen) return null;

  const buttonStyle = (
    variant:
      | "primary"
      | "secondary"
      | "success"
      | "warning"
      | "danger" = "primary"
  ): React.CSSProperties => {
    const variants = {
      primary: { background: "#3b82f6", color: "white" },
      secondary: { background: "#e5e7eb", color: "#374151" },
      success: { background: "#10b981", color: "white" },
      warning: { background: "#f59e0b", color: "white" },
      danger: { background: "#ef4444", color: "white" },
    };

    return {
      padding: "6px 12px",
      borderRadius: "6px",
      border: "none",
      fontSize: "12px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      ...variants[variant],
      minWidth: "80px",
      textAlign: "center" as const,
    };
  };

  return (
    <>
      <div className="lab-history-container">
        {/* Header */}
        <div className="lab-history-header">
          <h2>🧪 Lab Requisition History</h2>
          <p>
            All lab requisitions for{" "}
            {patient.full_name || `${patient.first_name} ${patient.last_name}`}
          </p>
        </div>

        {/* Content */}
        <div className="lab-history-content">
          {isLoading ? (
            <div className="loading-state">
              <div className="spinner" />
              Loading lab requisitions...
            </div>
          ) : error ? (
            <div className="error-state">
              ⚠️ {error}
              <br />
              <button
                onClick={loadRequisitions}
                style={{ ...buttonStyle("primary"), marginTop: "16px" }}
              >
                Retry
              </button>
            </div>
          ) : requisitions.length === 0 ? (
            <div className="empty-state">
              🧪 No lab requisitions found for this patient
            </div>
          ) : (
            <div className="requisitions-list">
              {requisitions.map((requisition) => (
                <div key={requisition.id} className="requisition-card">
                  {/* Requisition Header */}
                  <div className="requisition-header">
                    <div className="requisition-info">
                      <div className="requisition-title">
                        <h3>
                          {LabRequisitionService.getStatusIcon(
                            requisition.status
                          )}
                          Requisition #{requisition.requisition_number}
                        </h3>
                        <div className="badges">
                          <span
                            className="status-badge"
                            style={{
                              background:
                                LabRequisitionService.getStatusColor(
                                  requisition.status
                                ) + "20",
                              color: LabRequisitionService.getStatusColor(
                                requisition.status
                              ),
                            }}
                          >
                            {LabRequisitionService.getStatusLabel(
                              requisition.status
                            )}
                          </span>
                          <span
                            className="priority-badge"
                            style={{
                              background:
                                LabRequisitionService.getPriorityColor(
                                  requisition.priority
                                ) + "20",
                              color: LabRequisitionService.getPriorityColor(
                                requisition.priority
                              ),
                            }}
                          >
                            {LabRequisitionService.getPriorityIcon(
                              requisition.priority
                            )}
                            {LabRequisitionService.getPriorityLabel(
                              requisition.priority
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="requisition-details">
                        <div className="detail-item">
                          <strong>Date:</strong>{" "}
                          {LabRequisitionService.formatDate(
                            requisition.requisition_date
                          )}
                        </div>
                        <div className="detail-item">
                          <strong>Doctor:</strong> Dr.{" "}
                          {requisition.doctor?.name || "Unknown"}
                        </div>
                        {/* <div className="detail-item">
                          <strong>Tests:</strong> {requisition.items.length}{" "}
                          examination(s)
                        </div>
                        <div className="detail-item">
                          <strong>Total Cost:</strong>{" "}
                          {LabRequisitionService.formatCurrency(
                            requisition.estimated_total_cost
                          )}
                        </div> */}
                        {requisition.external_lab_name && (
                          <div className="detail-item">
                            <strong>External Lab:</strong>{" "}
                            {requisition.external_lab_name}
                          </div>
                        )}
                      </div>

                      {requisition.clinical_indication && (
                        <div className="clinical-indication">
                          <strong>Clinical Indication:</strong>{" "}
                          {requisition.clinical_indication}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="action-buttons">
                      {requisition.status === "draft" && (
                        <button
                          onClick={() =>
                            handleAction(
                              requisition.id!,
                              "issue",
                              LabRequisitionService.issueLabRequisition
                            )
                          }
                          disabled={actionLoading[requisition.id!]?.issue}
                          style={{
                            ...buttonStyle("primary"),
                            opacity: actionLoading[requisition.id!]?.issue
                              ? 0.7
                              : 1,
                          }}
                        >
                          {actionLoading[requisition.id!]?.issue
                            ? "📋 Issuing..."
                            : "📋 Issue"}
                        </button>
                      )}

                      {requisition.status === "issued" && (
                        <button
                          onClick={() =>
                            handleAction(
                              requisition.id!,
                              "complete",
                              LabRequisitionService.completeLabRequisition
                            )
                          }
                          disabled={actionLoading[requisition.id!]?.complete}
                          style={{
                            ...buttonStyle("success"),
                            opacity: actionLoading[requisition.id!]?.complete
                              ? 0.7
                              : 1,
                          }}
                        >
                          {actionLoading[requisition.id!]?.complete
                            ? "✅ Completing..."
                            : "✅ Complete"}
                        </button>
                      )}

                      {(requisition.status === "draft" ||
                        requisition.status === "issued") && (
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                "Are you sure you want to cancel this requisition?"
                              )
                            ) {
                              handleAction(
                                requisition.id!,
                                "cancel",
                                LabRequisitionService.cancelLabRequisition
                              );
                            }
                          }}
                          disabled={actionLoading[requisition.id!]?.cancel}
                          style={{
                            ...buttonStyle("danger"),
                            opacity: actionLoading[requisition.id!]?.cancel
                              ? 0.7
                              : 1,
                          }}
                        >
                          {actionLoading[requisition.id!]?.cancel
                            ? "❌ Cancelling..."
                            : "❌ Cancel"}
                        </button>
                      )}

                      <button
                        onClick={() => handleGenerateAndPrint(requisition.id!)}
                        disabled={actionLoading[requisition.id!]?.print}
                        style={{
                          ...buttonStyle("secondary"),
                          opacity: actionLoading[requisition.id!]?.print
                            ? 0.7
                            : 1,
                        }}
                      >
                        {actionLoading[requisition.id!]?.print
                          ? "🖨️ Preparing..."
                          : "🖨️ Generate & Print"}
                      </button>
                    </div>
                  </div>

                  {/* Requisition Details */}
                  <div className="requisition-body">
                    {/* Examinations List */}
                    <div className="examinations-section">
                      <h4>Examinations:</h4>
                      <div className="examinations-list">
                        {requisition.items.map((item, index) => (
                          <div key={index} className="examination-item">
                            <div className="examination-details">
                              <div className="examination-name">
                                <strong>
                                  {item.examination?.name || "Unknown Test"}
                                </strong>
                                {item.is_urgent && (
                                  <span className="urgent-badge">
                                    🚨 URGENT
                                  </span>
                                )}
                              </div>
                              {/* <div className="examination-meta">
                                <div className="meta-item">
                                  <strong>Specimen:</strong>{" "}
                                  {item.examination?.specimen_type || "N/A"}
                                </div>
                                <div className="meta-item">
                                  <strong>Cost:</strong>{" "}
                                  {LabRequisitionService.formatCurrency(
                                    item.estimated_cost
                                  )}
                                </div>
                              </div> */}
                            </div>

                            {item.special_instructions && (
                              <div className="special-instructions">
                                📝 Instructions: {item.special_instructions}
                              </div>
                            )}

                            {item.examination?.preparation_instructions && (
                              <div className="preparation-instructions">
                                💡 Preparation:{" "}
                                {item.examination.preparation_instructions}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Patient Instructions */}
                    {requisition.patient_instructions && (
                      <div className="patient-instructions">
                        <div className="section-title">
                          📋 Patient Instructions:
                        </div>
                        <div>{requisition.patient_instructions}</div>
                      </div>
                    )}

                    {/* Notes */}
                    {requisition.notes && (
                      <div className="notes-section">
                        <div className="section-title">📝 Notes:</div>
                        <div>{requisition.notes}</div>
                      </div>
                    )}

                    {/* Timeline Information */}
                    <div className="timeline-info">
                      <div className="timeline-item">
                        Created:{" "}
                        {LabRequisitionService.formatDateTime(
                          requisition.created_at!
                        )}
                      </div>
                      <div className="timeline-actions">
                        {requisition.issued_at && (
                          <span className="timeline-item">
                            Issued:{" "}
                            {LabRequisitionService.formatDateTime(
                              requisition.issued_at
                            )}
                          </span>
                        )}
                        {requisition.completed_at && (
                          <span className="timeline-item">
                            Completed:{" "}
                            {LabRequisitionService.formatDateTime(
                              requisition.completed_at
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary */}
          {requisitions.length > 0 && (
            <div className="summary-section">
              Total: {requisitions.length} requisition(s) • Draft:{" "}
              {requisitions.filter((r) => r.status === "draft").length} •
              Issued: {requisitions.filter((r) => r.status === "issued").length}{" "}
              • Completed:{" "}
              {requisitions.filter((r) => r.status === "completed").length}
            </div>
          )}
        </div>
      </div>

      <style>
        {`
        .lab-history-container {
          background: #ffffff;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          max-height: 600px;
          overflow: auto;
        }

        .lab-history-header {
          padding: 0 0 20px 0;
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 20px;
        }

        .lab-history-header h2 {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 4px 0;
          color: #1f2937;
        }

        .lab-history-header p {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }

        .loading-state, .error-state, .empty-state {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
          flex-direction: column;
          gap: 12px;
          text-align: center;
          font-size: 14px;
        }

        .error-state {
          color: #ef4444;
        }

        .empty-state {
          color: #6b7280;
        }

        .spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #e2e8f0;
          border-top: 3px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .requisitions-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .requisition-card {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
          transition: box-shadow 0.2s ease;
        }

        .requisition-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .requisition-header {
          background: #f8fafc;
          padding: 16px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .requisition-info {
          flex: 1;
        }

        .requisition-title {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 12px;
        }

        .requisition-title h3 {
          font-size: 15px;
          font-weight: 600;
          margin: 0;
          color: #1f2937;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .badges {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .status-badge, .priority-badge {
          padding: 3px 6px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 500;
          text-transform: capitalize;
          white-space: nowrap;
        }

        .requisition-details {
          display: grid;
          grid-template-columns: 1fr;
          gap: 8px;
          font-size: 13px;
          color: #6b7280;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .clinical-indication {
          margin-top: 12px;
          padding: 8px 12px;
          background: #eff6ff;
          border: 1px solid #dbeafe;
          border-radius: 6px;
          font-size: 13px;
        }

        .action-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          justify-content: flex-start;
        }

        .requisition-body {
          padding: 16px;
        }

        .examinations-section {
          margin-bottom: 16px;
        }

        .examinations-section h4 {
          font-size: 14px;
          font-weight: 600;
          margin: 0 0 12px 0;
          color: #374151;
        }

        .examinations-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .examination-item {
          background: #f9fafb;
          border: 1px solid #f3f4f6;
          border-radius: 6px;
          padding: 12px;
        }

        .examination-details {
          margin-bottom: 8px;
        }

        .examination-name {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          flex-wrap: wrap;
        }

        .examination-name strong {
          color: #1f2937;
          font-size: 13px;
        }

        .urgent-badge {
          padding: 2px 6px;
          background: #fecaca;
          color: #dc2626;
          border-radius: 4px;
          font-size: 9px;
          font-weight: 500;
        }

        .examination-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          font-size: 12px;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .special-instructions {
          margin-top: 8px;
          font-size: 11px;
          color: #6b7280;
          font-style: italic;
        }

        .preparation-instructions {
          margin-top: 4px;
          font-size: 11px;
          color: #1e40af;
          font-style: italic;
        }

        .patient-instructions, .notes-section {
          background: #fef9e7;
          border: 1px solid #fde68a;
          border-radius: 6px;
          padding: 12px;
          margin-bottom: 12px;
        }

        .notes-section {
          background: #f0f9ff;
          border: 1px solid #dbeafe;
        }

        .section-title {
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 4px;
          color: #92400e;
        }

        .notes-section .section-title {
          color: #1e40af;
        }

        .patient-instructions div:last-child,
        .notes-section div:last-child {
          font-size: 12px;
          color: #78350f;
        }

        .notes-section div:last-child {
          color: #1e40af;
        }

        .timeline-info {
          display: flex;
          flex-direction: column;
          gap: 8px;
          font-size: 11px;
          color: #6b7280;
          padding-top: 12px;
          border-top: 1px solid #f3f4f6;
        }

        .timeline-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
        }

        .timeline-item {
          white-space: nowrap;
        }

        .summary-section {
          margin-top: 24px;
          padding: 16px;
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          text-align: center;
          font-size: 13px;
          color: #6b7280;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Tablet styles */
        @media (min-width: 640px) {
          .lab-history-container {
            padding: 20px;
          }

          .lab-history-header h2 {
            font-size: 20px;
          }

          .lab-history-header p {
            font-size: 14px;
          }

          .requisition-header {
            flex-direction: row;
            align-items: flex-start;
          }

          .requisition-title {
            flex-direction: row;
            align-items: center;
          }

          .badges {
            margin-left: 12px;
          }

          .requisition-details {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            font-size: 14px;
          }

          .action-buttons {
            flex-direction: column;
            margin-left: 16px;
          }

          .examination-meta {
            gap: 20px;
          }

          .timeline-info {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }
        }

        /* Desktop styles */
        @media (min-width: 1024px) {
          .requisition-details {
            grid-template-columns: repeat(3, 1fr);
          }

          .examination-name {
            flex-wrap: nowrap;
          }

          .timeline-actions {
            gap: 20px;
          }
        }

        button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}
      </style>
    </>
  );
};

export default LabRequisitionHistory;
